import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/utils/cn';
import { BaytarcLogo } from '@/components/brand/BaytarcLogo';
import { MenuToggle } from '@/components/layout/MenuToggle';
import { SearchBar } from '@/components/layout/SearchBar';
import { NotificationBell, type Notification } from '@/components/layout/NotificationBell';
import { UserMenu } from '@/components/layout/UserMenu';
import OrgSelector from '@/components/layout/OrgSelector';
import { useNotificationSocket } from '@/hooks/useWebSocket';
import { useUnreadCount } from '@/hooks/useNotificationsApi';
import { QUERY_KEYS } from '@/hooks/queryKeys';
import { whatsappMessagesKey } from '@/hooks/useWhatsapp';
import toast from 'react-hot-toast';

export interface HeaderProps {
  className?: string;
  onMenuToggle?: (isOpen: boolean) => void;
  onSearch?: (query: string) => void;
  onCommandPalette?: () => void;
  notifications?: Notification[];
  onNotificationAction?: (action: string, notificationId?: string) => void;
  onUserAction?: (action: string) => void;
}

const Header: React.FC<HeaderProps> = ({
  className,
  onMenuToggle,
  onSearch,
  onCommandPalette,
  notifications: _notifications = [],
  onNotificationAction,
  onUserAction,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.unreadCount ?? 0;

  // Real-time: invalidate unread count when a new notification arrives via WebSocket
  useNotificationSocket({
    onNotification: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.unreadCount() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.lists() });
    },
    onCrmMessage: (message) => {
      queryClient.invalidateQueries({ queryKey: whatsappMessagesKey(message.leadId) });
      toast('New WhatsApp message from a lead', { icon: '💬' });
    },
  });

  const handleMenuToggle = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    if (onMenuToggle) {
      onMenuToggle(newState);
    }
  };

  const handleNotificationMarkAsRead = (id: string) => {
    if (onNotificationAction) {
      onNotificationAction('markAsRead', id);
    }
  };

  const handleNotificationMarkAllAsRead = () => {
    if (onNotificationAction) {
      onNotificationAction('markAllAsRead');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (onNotificationAction) {
      onNotificationAction('click', notification.id);
    }
  };

  const handleUserProfile = () => {
    if (onUserAction) {
      onUserAction('profile');
    }
  };

  const handleUserSettings = () => {
    if (onUserAction) {
      onUserAction('settings');
    }
  };

  const handleUserLogout = () => {
    if (onUserAction) {
      onUserAction('logout');
    }
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50',
        'h-16 bg-white border-b border-secondary-200 shadow-sm',
        'transition-all duration-300',
        className
      )}
    >
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          {/* Mobile Menu Toggle */}
          <div className="lg:hidden">
            <MenuToggle isOpen={isMobileMenuOpen} onClick={handleMenuToggle} />
          </div>

          {/* Logo */}
          <div className="hidden sm:flex items-center">
            <BaytarcLogo variant="navy" size="md" />
          </div>
          <div className="sm:hidden">
            <BaytarcLogo variant="navy" iconOnly size="md" />
          </div>

          {/* Search Bar - Hidden on mobile */}
          <div className="hidden md:block">
            <SearchBar
              onSearch={onSearch}
              onCommandPalette={onCommandPalette}
              className="w-64 lg:w-80"
            />
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-3">
          {/* Enhanced Mobile Search Button */}
          <button
            onClick={onCommandPalette}
            className="md:hidden p-2.5 text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100/80 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-sm hover:shadow-md"
            aria-label="Search"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>

          {/* Org Selector, visible for SUPER_ADMIN only */}
          <div className="hidden sm:block">
            <OrgSelector />
          </div>

          {/* Notification Bell, badge count from API, real-time via WebSocket */}
          <NotificationBell
            unreadCount={unreadCount}
            onMarkAsRead={handleNotificationMarkAsRead}
            onMarkAllAsRead={handleNotificationMarkAllAsRead}
            onNotificationClick={handleNotificationClick}
          />

          {/* User Menu */}
          <UserMenu
            onProfile={handleUserProfile}
            onSettings={handleUserSettings}
            onLogout={handleUserLogout}
          />
        </div>
      </div>
    </header>
  );
};

export default Header;
